/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */

import { createEntityAdapter, createSelector, type EntityState } from "@reduxjs/toolkit"
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { makeError, type ModelError } from "@/lib/models/errors"
import { translate as t} from "@/i18n/client_nohook";
import { AuthStates, getAuthState } from "@/lib/auth/auth_state";
import { responseForAPIError } from "@/lib/datastore/slices/helpers";
import type { IodyneProjectInfo } from "@/lib/models/projects";
import { devicesSlice } from "./devices";
import { orgSlice } from "./org";
import {
    ACLAccessLevel,
    ACLResourceType,
    ACLSubjectType,
    type ACL,
    type BaseACL,
    type Claimcheck,
    type ProjectACL,
    type ProjectClaimchecks
} from "@/lib/models/acls";
import type { MetadataBundle, MetadataField } from "@/lib/models/metadata";
import type { DeviceState } from "@/lib/models/devices";

// Constants
enum ProjectAPIErrors {
    projectDNE = "ProjectDNEError"
}

// Slices
const projectsAdapter = createEntityAdapter({
  selectId: (e:IodyneProjectInfo) => e.project_id
});

const aclsAdapter = createEntityAdapter({
    selectId:(e:ProjectACL) => e.acl_id
});

const claimchecksAdapter = createEntityAdapter({
    selectId: (e:ProjectClaimchecks) => e.project_id
});

const projectsInitialState = projectsAdapter.getInitialState();
const aclsInitialState = aclsAdapter.getInitialState();
const claimchecksInitialState = claimchecksAdapter.getInitialState();

export const projectsSlice = createApi({
    reducerPath: "projects_api",
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.IOD_CLOUD_PORTAL_PROJECTS_API_URI}/v1/`,
        prepareHeaders: (headers, { getState }) => {
            headers.set("Authorization", `Bearer ${getAuthState(AuthStates.userAccessToken)}`)
            return headers;
        }
    }),
    tagTypes: [
        "ProjectDetail",
        "ProjectDevices",
        "ProjectMetadataBundleFields",
        "ProjectMetadataBundles",
        "ProjectACLs",
        "ProjectClaimchecks",
        "AccountProjectTags",
        "ProjectAccessLevel"
    ],
    endpoints: build => ({
        // Retrieve Projects from Account
        fetchProjects: build.query<EntityState<IodyneProjectInfo, string>, { org_id: string, project_ids: string[] }>({
            //@ts-ignore
            providesTags: (result, _, arg) => {
            if(!result) {
                return []
            }
            
            return [
                ...result.ids.map((project_id) => ({type:"ProjectDetail", id:project_id}))
            ]},

            //@ts-ignore
            queryFn: async (arg, queryApi, _extraOptions, baseQuery) => {
                var project_ids: string[] = arg.project_ids ? arg.project_ids : [];
                if (!project_ids.length) {
                    //Pull Project IDs for Org
                    const project_ids_resp = await baseQuery({
                        url: `projects`,
                        method: "GET",
                        params: { "org_id": arg.org_id }
                    });
                    if (project_ids_resp.error) return { error: project_ids_resp.error }

                    project_ids = project_ids_resp.data as string[];
                }

                //Retrieve Project Details for IDs
                var projects: IodyneProjectInfo[] = [];
                for (const proj_id of project_ids) {
                    const project_detail_resp = await baseQuery({
                        url: `project/${proj_id}`,
                        method: "GET"
                    });
                    if (project_detail_resp.error) return { error: project_detail_resp.error }

                    projects.push(project_detail_resp.data as IodyneProjectInfo);
                }
                
                return { data: projectsAdapter.setAll(projectsInitialState, projects), error: null };
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                );
            }
        }),

        // Archive Project
        archiveProject: build.mutation<IodyneProjectInfo, {org_id:string, project_id:string}>({
            invalidatesTags: (result, error, org_id, project_id) => [
                { type: "ProjectDetail", org_id:org_id, project_id: project_id },
                { type: "ProjectDevices" }
            ],
            query: ({org_id, project_id}) => ({
                url: `project/${project_id}/archive`,
                method: "GET",
                params: {"org_id" : org_id}
            })
        }),

        // Unarchive Project
        unarchiveProject: build.mutation<IodyneProjectInfo, {org_id:string, project_id:string}>({
            invalidatesTags: (result, error, org_id, project_id) => [
                { type: "ProjectDetail", org_id:org_id, project_id: project_id },
                { type: "ProjectDevices" }
            ],
            query: ({org_id, project_id}) => ({
                url: `project/${project_id}/unarchive`,
                method: "GET",
                params: {"org_id" : org_id}
            })
        }),

        // Retrieve Devices for Project
        fetchDevicesforProject: build.query<Array<[string, ACLAccessLevel, DeviceState]>, {org_id:string, project_id:string}>({
            providesTags: (result,_,arg) => [{type: "ProjectDevices", id:arg.project_id}],
            query: ({org_id, project_id}) => ({
                url: `project/${project_id}/devices`,
                method: "GET",
                params: { "org_id" : org_id }
            }),
        }),

        // Retrieve Metadata Bundles
        fetchMetadataBundles: build.query<MetadataBundle, {org_id:string, project_id:string}>({
            providesTags: (result,_,arg) => [{
                type:"ProjectMetadataBundles",
                id:arg.project_id
            }],
            query: ({org_id, project_id}) => ({
                url: `project/${project_id}/metadata/bundles`,
                method: "GET",
                params: { "org_id": org_id }
            }),
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        // Retrieve Metadata Fields for Bundle in Project
        fetchMetadataBundleFields: build.query<MetadataField[], {org_id:string, project_id:string, bundle_id:string}>({
            providesTags: (_result,_meta,arg) => [{ 
                type:"ProjectMetadataBundleFields", 
                id:`${arg.project_id}.${arg.bundle_id}`
            }],
            query: ({org_id, project_id, bundle_id}) => ({
                url: `project/${project_id}/metadata/${bundle_id}`,
                method: "GET",
                params: { "org_id": org_id }
            }),
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        // Delete Metadata Field in Bundle
        deleteMetadataField: build.mutation<boolean, {org_id:string, project_id:string, bundle_id:string, field_id:string}>({
            invalidatesTags: (result,_,arg) => [{
                type:"ProjectMetadataBundleFields",
                id:`${arg.project_id}.${arg.bundle_id}`
            }],
            query: ({org_id, project_id, bundle_id, field_id}) => ({
                url: `project/${project_id}/metadata/${bundle_id}/${field_id}`,
                method: "DELETE",
                params: { "org_id": org_id }
            }),
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        // Modify Metadata Field in Bundle
        modifyMetadataField: build.mutation<MetadataField[], {org_id:string, project_id:string, bundle_id:string, fields:MetadataField[]}>({
            invalidatesTags: (result,_,arg) => [{
                type:"ProjectMetadataBundleFields",
                id:`${arg.project_id}.${arg.bundle_id}`
            }],
            query: ({org_id, project_id, bundle_id, fields}) => ({
                url: `project/${project_id}/metadata/${bundle_id}`,
                method: "PUT",
                params: { "org_id": org_id },
                body: {fields: fields}
            }),
            async transformErrorResponse(resp: { status: number, data: Record<string, any>}): Promise<ModelError> {
                return responseForAPIError(resp, {});
            }
        }),

        // Retrieve ACLs for Project
        fetchProjectAccessLevel: build.query<ACLAccessLevel, {project_id:string}>({
            providesTags: (result,_,arg) => [{type: "ProjectACLs", id:arg.project_id}],
            query: ({project_id}) => ({
                url: `project/${project_id}/auth/access_level`,
                method: "GET",
            }),
            async transformErrorResponse(resp: { status: number, data: Record<string, any>}): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        // Retrieve ACLs for Project
        fetchACLsforProject: build.query<EntityState<ProjectACL, string>, {org_id:string, project_id:string}>({
            providesTags: (result,_,arg) => [{type: "ProjectACLs", id:arg.project_id}],
            query: ({org_id, project_id}) => ({
                url: `project/${project_id}/auth/acls`,
                method: "GET",
                params: { "org_id" : org_id }
            }),
            transformResponse: (resp:ACL[], _, {project_id}) => {
                return aclsAdapter.setMany(
                    aclsInitialState, 
                    resp.map((acl:ACL) => ({ ...acl, ...{project_id: project_id}}))
                );
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any>}): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        //Add ACLs
        addACLsToProject: build.mutation<EntityState<ProjectACL, string>, {org_id:string, project_id:string, acls:BaseACL[]}>({
            invalidatesTags: (result, error,arg) => [ {type: "ProjectACLs", id:arg.project_id} ],
            query: ({org_id, project_id, acls}) => ({
                url: `project/${project_id}/auth/acls`,
                method: "POST",
                params: { "org_id" : org_id },
                body: { "acls" : acls}
            }),
        }),

        //Delete ACLs
        deleteACLsFromProject: build.mutation<EntityState<ProjectACL, string>, {org_id:string, project_id:string, acl_ids:string[]}>({
            invalidatesTags: (result, error,arg) => [ {type: "ProjectACLs", id:arg.project_id} ],
            query: ({ org_id, project_id, acl_ids }) => {
                const params = new URLSearchParams();
                params.append("org_id", org_id);
                acl_ids.forEach((acl_id) => params.append("acl_id", acl_id))

                return {
                    url: `project/${project_id}/auth/acls`,
                    method: "DELETE",
                    params: params
                }
            },
        }),

        //Modify ACLs
        modifyACLsOnProject: build.mutation<EntityState<ProjectACL, string>, {org_id:string, project_id:string, acls:BaseACL[]}>({
            invalidatesTags: (result, error,arg) => [ {type: "ProjectACLs", id:arg.project_id} ],
            query: ({org_id, project_id, acls}) => ({
                url: `project/${project_id}/auth/acls`,
                method: "PUT",
                params: { "org_id" : org_id },
                body: { "acls" : acls}
            }),
        }),

        // Create New Project
        createProject: build.mutation<EntityState<IodyneProjectInfo, string>, {org_id: string, project_name:string}>({
            //@ts-ignore
            providesTags: (result,_,arg) => [{type: "ProjectDetail", org_id:org_id, project_id: project_id}],
            invalidatesTags: (result, error,_org_id,_project_name) => [ {type: "ProjectDetail" } ],
            query: ({org_id, project_name}) => ({
                url: "project",
                method: "POST",
                params: {"org_id" : org_id},
                body: { "project_name" : project_name }
            }),
            transformResponse: (resp:IodyneProjectInfo) => {
                return projectsAdapter.setOne(projectsInitialState, resp);
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any>}): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        // Modify Project Details
        modifyProjectDetails: build.mutation<EntityState<IodyneProjectInfo, string>, {org_id: string, project_id: string, details: IodyneProjectInfo}>({
            invalidatesTags: (result, error, org_id, project_id) => [
                { type: "ProjectDetail", org_id:org_id, project_id: project_id},
                { type: "AccountProjectTags" }
            ],
            query: ({ org_id, project_id, details }) => ({
                url: `project/${project_id}`,
                method: "PUT",
                params: { "org_id" : org_id },
                body: details
            }),
            transformResponse: (resp:IodyneProjectInfo) => {
                return projectsAdapter.setOne(projectsInitialState, resp);
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any>}): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        setCoverArt: build.mutation<EntityState<IodyneProjectInfo, string>, {org_id: string, project_id: string, image:File|undefined}>({
            invalidatesTags: (result, error, org_id, project_id) => [
                { type: "ProjectDetail", org_id:org_id, project_id: project_id}
            ],
            query: ({ org_id, project_id, image }) => {
                let body_form_data = new FormData();
                image && body_form_data.append("image", image);
                return {
                    url: `project/${project_id}/coverart`,
                    method: "POST",
                    params: { "org_id": org_id },
                    body: body_form_data,
                    formData: true
                }
            }
        }),

        //Add / Remove Devices from Project
        modifyDevicesList: build.mutation<Array<string>, {org_id:string, project_id:string, add_devices:string[], remove_devices:string[]}>({
            invalidatesTags: ["ProjectDevices", "ProjectACLs"],
            //@ts-ignore
            queryFn: async(arg, queryApi, _extraOptions, baseQuery) => {
                const add_remove_result = await baseQuery({
                    url: `project/${arg.project_id}/devices`,
                    method: "POST",
                    params: { "org_id": arg.org_id },
                    body: {
                        "add_devices": arg.add_devices,
                        "remove_devices": arg.remove_devices
                    }
                });
                if (add_remove_result.error) return { error: add_remove_result.error };

                // Invalidate Devices & Account APIs device cache entries
                queryApi.dispatch(devicesSlice.util.invalidateTags([{type: "Device"}]));
                queryApi.dispatch(orgSlice.util.invalidateTags([{type: "Device"}]));

                return { data: add_remove_result.data};
            }
        }),

        //List All Project Tags
        listAllProjectTags: build.query<Array<string>, {org_id:string}>({
            providesTags: (result,_,arg) => [{type: "AccountProjectTags", id:arg.org_id}],
            query: ({org_id}) => ({
                url: "tags",
                method: "GET",
                params: { "org_id": org_id}
            })
        }),

        //List All Claimchecks
        listAllProjectClaimchecks: build.query<
            EntityState<ProjectClaimchecks, string>, 
            {org_id:string, project_id:string, filter_revoked_status?:boolean, filter_caller_claimchecks_only?:boolean}
        >({
            providesTags: (result, _, arg) => [{ type: "ProjectClaimchecks", id: arg.project_id }],
            query: ({ org_id, project_id, filter_revoked_status, filter_caller_claimchecks_only }) => {
                const params = new URLSearchParams();
                params.append("org_id", org_id);
                if (filter_revoked_status !== undefined) {
                    params.append("filter_revoked_status", filter_revoked_status ? "true" : "false")
                }
                if (filter_caller_claimchecks_only !== undefined) {
                    params.append("filter_caller_claimchecks_only", filter_caller_claimchecks_only ? "true" : "false")
                }

                return {
                    url: `project/${project_id}/auth/claimchecks`,
                    method: "GET",
                    params: params
                }
            },
            transformResponse: (resp: Claimcheck[], _, { project_id }) => {
                return claimchecksAdapter.setOne(
                    claimchecksInitialState,
                    { project_id: project_id, claimchecks: resp } as ProjectClaimchecks
                );
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any>}): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    { [ProjectAPIErrors.projectDNE]: await t("project_mgmt.api_errors.project_dne") }
                )
            }
        }),

        //Modify a Claimcheck
        modifyProjectClaimcheck: build.mutation<
            EntityState<ProjectClaimchecks, string>, 
            {org_id:string, project_id:string, cc_id:string, cc_name?:string, revoke?:boolean}
        >({
            invalidatesTags: (result, error, org_id, project_id) => [{ type: "ProjectClaimchecks" }],
            query: ({org_id, project_id, cc_id, cc_name, revoke}) => {
                var req_body:any = {};
                if(cc_name !== undefined) {
                    req_body["cc_name"] = cc_name;
                }
                if(revoke !== undefined) {
                    req_body["revoke"] = revoke;
                }

                return {
                    url: `project/${project_id}/auth/claimcheck/${cc_id}`,
                    method: "PUT",
                    params: {org_id: org_id},
                    body: req_body
                }
            }
        })
    })
});

export const {
    useFetchProjectsQuery,
    useFetchDevicesforProjectQuery,
    useModifyProjectDetailsMutation,
    useModifyDevicesListMutation,
    useListAllProjectTagsQuery,
    useCreateProjectMutation,
    useSetCoverArtMutation,
    useFetchACLsforProjectQuery,
    useAddACLsToProjectMutation,
    useDeleteACLsFromProjectMutation,
    useModifyACLsOnProjectMutation,
    useListAllProjectClaimchecksQuery,
    useModifyProjectClaimcheckMutation,
    useFetchMetadataBundlesQuery,
    useFetchMetadataBundleFieldsQuery,
    useDeleteMetadataFieldMutation,
    useModifyMetadataFieldMutation,
    useFetchProjectAccessLevelQuery,
    useArchiveProjectMutation,
    useUnarchiveProjectMutation
} = projectsSlice;

export const projectsSelectors = projectsAdapter.getSelectors();
export const aclsSelectors = aclsAdapter.getSelectors();

// 
export type ACLSubjectMapType = {
    subject_type: ACLSubjectType, 
    subject_id: string, 
    acls:ProjectACL[]
};
export type ACLSubjectMap = {[key: string]: ACLSubjectMapType};

export const aclsBySubjectID = (project_id: string) => createSelector(
    aclsSelectors.selectEntities,
    (acls: { [key: string]: ProjectACL }):ACLSubjectMap => {
        var acls_subject_map:ACLSubjectMap = {};

        Object.keys(acls).forEach((acl_id:string) => {
            const subject_id = acls[acl_id].subject_id;
            if(!(subject_id in acls_subject_map)) {
                acls_subject_map[subject_id] = {
                    subject_type: acls[acl_id].subject_type,
                    subject_id: acls[acl_id].subject_id,
                    acls: []
                }
            }
            acls_subject_map[subject_id]["acls"].push(acls[acl_id]);
        })

        return acls_subject_map;
    }
);

export function aclsForResource(
    acls:ProjectACL[], 
    resource_type:ACLResourceType, 
    resource_id?:string
):ProjectACL[] {
    return acls.filter((acl) => (
        acl.resource_type === resource_type && 
        (resource_id === undefined || acl.resource_id == resource_id)
    ))
}